// Recipe usage units (2026-09-27, bdr_mobile_app/plans/recipe_usage_units_2026_09_27.md rev 2).
//
// "Contents" = 1 stock unit holds N pieces (1 pack = 100 filters). A lens for
// ENTERING and READING per-door quantities; the stored qtyPerDoor is always in
// stock units, so nothing here ever touches cost math. Mirrors
// server/utils/inventoryContents.js.

/** { ppu, name } when the item has usable contents, else null. */
export function contentsOf(item) {
  if (!item) return null;
  const ppu = parseFloat(item.piecesPerUnit);
  const name = (item.pieceName || "").trim();
  return Number.isFinite(ppu) && ppu > 0 && name ? { ppu, name } : null;
}

/** Display a derived number: 4 significant figures, no trailing zeros, no float dust (0.07*100). */
export function fmtQty(n, sig = 4) {
  if (n == null || !Number.isFinite(n)) return "";
  if (n === 0) return "0";
  const v = Number(n.toPrecision(sig));
  // Avoid exponent notation for small values in inputs/labels.
  return Math.abs(v) < 1e-6 ? v.toFixed(10).replace(/0+$/, "").replace(/\.$/, "") : String(v);
}

/**
 * "1 filter" / "1.5 filters" / "2 boxes". Leaves alone what shouldn't change:
 * "each", anything already ending in s ("pcs", "gloves"), and short or vowel-less
 * abbreviations ("oz", "ft", "gal", "qt").
 */
export function pluralize(name, n) {
  if (!name) return "";
  if (n === 1) return name;
  const lower = name.toLowerCase();
  if (lower === "each" || /s$/i.test(name) || name.length <= 3 || !/[aeiou]/i.test(name)) return name;
  if (/(x|z|ch|sh)$/i.test(name)) return `${name}es`;
  if (/[^aeiou]y$/i.test(name)) return `${name.slice(0, -1)}ies`;
  return `${name}s`;
}

/** Stock unit as a noun for labels: "each" reads as "item" ("doors per item"). */
export function unitNoun(unit) {
  const u = (unit || "each").trim();
  return u.toLowerCase() === "each" ? "item" : u;
}

/**
 * Per-line entry modes for the recipe editor:
 *   pieces — value pieces per door            qty = value / ppu
 *   stock  — value stock units per door       qty = value
 *   lasts  — 1 stock unit lasts value doors   qty = 1 / value
 * Returns the canonical stock-unit qty, or null when the value isn't usable.
 */
export function qtyFromEntry(mode, value, contents) {
  const v = parseFloat(value);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (mode === "pieces") return contents ? v / contents.ppu : null;
  if (mode === "lasts") return 1 / v;
  return v;
}

/** The display value for a canonical qty in a given mode (rounded for display only). */
export function entryFromQty(mode, qty, contents) {
  if (qty == null || !Number.isFinite(qty) || qty <= 0) return "";
  if (mode === "pieces") return contents ? fmtQty(qty * contents.ppu) : "";
  if (mode === "lasts") return fmtQty(1 / qty);
  return fmtQty(qty);
}

/** Default mode for an item: pieces when it has contents, else stock. */
export function defaultMode(item) {
  return contentsOf(item) ? "pieces" : "stock";
}

/** Human line for a per-door stock qty: "1.5 filters (0.015 pack)" or "0.25 gallon". */
export function describePerDoor(qty, stockUnit, contents) {
  const q = typeof qty === "number" ? qty : parseFloat(qty);
  if (!Number.isFinite(q)) return "--";
  const unit = unitNoun(stockUnit);
  if (contents) {
    const pieces = q * contents.ppu;
    return `${fmtQty(pieces)} ${pluralize(contents.name, Number(fmtQty(pieces)))} (${fmtQty(q)} ${unit})`;
  }
  return `${fmtQty(q)} ${unit}`;
}

/** The one "contents looks wrong" rule, shared by both item editors (UI #9). */
export function contentsWarning({ piecesPerUnit, pieceName, stockUnit, vendorPack }) {
  const ppu = parseFloat(piecesPerUnit);
  const unit = (stockUnit || "each").trim().toLowerCase();
  if ((pieceName || "").trim() && (pieceName || "").trim().toLowerCase() === unit) {
    return { block: true, message: "The piece is the same as the stock unit, so contents isn't needed. Leave it blank." };
  }
  if (Number.isFinite(ppu) && vendorPack > 1 && ppu === vendorPack) {
    return {
      block: false,
      message: `Contents matches the vendor pack size (${vendorPack}). Receipts already turn 1 purchase into ${vendorPack} stock units — contents should count the pieces in ONE ${unitNoun(stockUnit)}, not the pack.`,
    };
  }
  return null;
}
